/**
 * Client side of the per-install device secret (Manage devices).
 *
 * The server issues a random 256-bit secret to this install the first time it
 * registers (we send `claimSecret: true`) and keeps only a SHA-256 of it. This module stores that secret per
 * (user, device id) and serialises every register/heartbeat call so a second
 * overlapping call can never go out before the first one's secret is saved.
 *
 * STORAGE (via `SecureStorage`, see services/secure-storage.ts)
 *  - iOS: the `AscendSecureStorage` file store — Application Support, flagged
 *    excluded-from-backup, `CompleteUntilFirstUserAuthentication`. It is NOT the
 *    Keychain; it gives the property that matters here (never in an iCloud /
 *    Finder backup or restored onto another phone), at the same at-rest class
 *    the app's Cognito tokens use. A restored backup therefore has the device
 *    id (UserDefaults) but not the secret → the server re-verifies it.
 *  - Android: Capacitor Preferences (app-private SharedPreferences). Excluded
 *    from cloud backup and device transfer by the manifest rules
 *    (docs/security-local-storage.md); protected by Android file-based
 *    encryption, not by an extra app-level key.
 *  - Web: Preferences' localStorage backend. Readable by any script on the
 *    origin (XSS) and cleared with site data — the weakest tier; a browser
 *    that loses it registers under a new device id and is re-approved.
 *
 * The secret is deliberately NOT removed at sign-out: the device row (and its
 * hash) outlives the session, and wiping it would make the next sign-in look
 * like a lost secret (new device id, new approval).
 */

import { getCurrentUser } from 'aws-amplify/auth'
import { SecureStorage } from './secure-storage'

export const INSTALL_SECRET_KEY_PREFIX = 'ascend-install-secret:'

/**
 * The signed-in user's id. Callers resolve it ONCE per serialised call and
 * pass it to read/save, so a secret is never read from or saved under a
 * different account's key if the account switches mid-call (shared phones).
 */
export async function getInstallSecretUserId(): Promise<string | null> {
  try {
    return (await getCurrentUser()).userId
  } catch {
    return null
  }
}

function keyFor(userId: string, deviceId: string): string {
  return `${INSTALL_SECRET_KEY_PREFIX}${userId}:${deviceId}`
}

/**
 * `readable: false` means the secure store itself failed — we do NOT know
 * whether a secret exists, so a "could not verify" answer must never be
 * treated as a lost secret (which moves the install to a new device id).
 */
export async function readInstallSecretState(
  userId: string | null,
  deviceId: string,
): Promise<{ secret: string | null; readable: boolean }> {
  if (!userId) return { secret: null, readable: true }
  try {
    const { value } = await SecureStorage.get({ key: keyFor(userId, deviceId) })
    return { secret: value || null, readable: true }
  } catch {
    return { secret: null, readable: false }
  }
}

export async function readInstallSecret(
  deviceId: string,
  userId?: string | null,
): Promise<string | null> {
  const id = userId === undefined ? await getInstallSecretUserId() : userId
  return (await readInstallSecretState(id, deviceId)).secret
}

export async function saveInstallSecret(
  deviceId: string,
  secret: string,
  userId?: string | null,
): Promise<void> {
  const id = userId === undefined ? await getInstallSecretUserId() : userId
  if (!id) return
  await SecureStorage.set({ key: keyFor(id, deviceId), value: secret })
}

// One register/heartbeat at a time per process.
let queue: Promise<unknown> = Promise.resolve()
export function serialiseDeviceCall<T>(task: () => Promise<T>): Promise<T> {
  const next = queue.then(task, task)
  queue = next.catch(() => undefined)
  return next
}

/** "Still registering": another call from this install is mid-claim — wait and retry. */
export function isStillRegisteringError(err: unknown): boolean {
  return /still registering/i.test(err instanceof Error ? err.message : String(err))
}

/**
 * The server rejected this install's secret (SECRET_INVALID). The row is NOT
 * changed by that. A client that holds no secret for its id (a backup restored
 * onto another phone, a cleared secret store) moves to a NEW device id and
 * registers as a new pending install instead of re-claiming the old row.
 */
export function isDeviceVerificationError(err: unknown): boolean {
  return /could not verify this device/i.test(err instanceof Error ? err.message : String(err))
}

export function __resetDeviceCallQueueForTests(): void {
  queue = Promise.resolve()
}
